import React, { useState } from "react";
import { useDispatch } from "react-redux";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid2 from "@mui/material/Grid2";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import { LabelText } from "../Common/Typography/AppTypography";
import Alert from "../layout/Alert";
import { setAlert } from "../../action/alert";
import {
  getAssetErrorMessage,
  resolveAssetViewMeta,
  viewAsset,
} from "../../action/asset";
import { selectMenuProps } from "../../utils/selectMenuProps";
import AssetPreview from "./AssetPreview";
import { ASSET_RESOLUTION_OPTIONS, formControlSx } from "./assetConstants";

function AssetViewForm() {
  const dispatch = useDispatch();
  const [assetId, setAssetId] = useState("");
  const [resolution, setResolution] = useState("");
  const [tag, setTag] = useState("");
  const [preview, setPreview] = useState({ url: "", contentType: "" });
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setAssetId("");
    setResolution("");
    setTag("");
    setPreview({ url: "", contentType: "" });
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPreview({ url: "", contentType: "" });

    if (!assetId?.trim()) {
      dispatch(setAlert("Asset ID Cannot be Empty", "danger"));
      return;
    }

    if (!resolution) {
      dispatch(setAlert("Resolution Cannot be Empty", "danger"));
      return;
    }

    if (!tag?.trim()) {
      dispatch(setAlert("Tag Cannot be Empty", "danger"));
      return;
    }

    setLoading(true);

    try {
      const res = await viewAsset(assetId.trim(), tag.trim(), resolution);
      const { url, contentType } = resolveAssetViewMeta(res?.data);
      if (url) {
        setPreview({ url, contentType });
      } else {
        dispatch(
          setAlert(
            "Asset not found. Please verify the Asset ID, Tag, and Resolution.",
            "danger",
          ),
        );
      }
    } catch (err) {
      dispatch(setAlert(getAssetErrorMessage(err, "Unable to load asset."), "danger"));
    }

    setLoading(false);
  };

  return (
    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
      <form onSubmit={handleSubmit}>
        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Asset ID:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              label="Enter Asset ID"
              placeholder="e.g. home-banner-test"
              value={assetId}
              onChange={(e) => setAssetId(e.target.value)}
              sx={formControlSx}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Tag:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              label="Enter Tag"
              placeholder="e.g. splash"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              sx={formControlSx}
            />
          </Grid2>
        </Grid2>

        <Grid2 container spacing={2} sx={{ mb: 3, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Resolution:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="select"
              size="small"
              fullWidth
              label="Select Resolution"
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              options={ASSET_RESOLUTION_OPTIONS}
              MenuProps={selectMenuProps}
              sx={formControlSx}
            />
          </Grid2>
        </Grid2>

        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <AppButton
            variant="primary"
            size="medium"
            type="submit"
            disabled={loading}
            loading={loading}
          >
            View Asset
          </AppButton>
          <AppButton
            variant="ghost"
            size="medium"
            type="button"
            onClick={resetForm}
            disabled={loading}
          >
            Reset
          </AppButton>
        </Box>
      </form>

      <Box sx={{ mt: 2 }}>
        <Alert />
      </Box>

      <AssetPreview
        url={preview.url}
        contentType={preview.contentType}
        loading={loading}
      />
    </Paper>
  );
}

export default AssetViewForm;
